import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { verifyEmail } from '@/api/auth.api';
import { isSignInRequired } from '@/lib/utils/sign-in-required.utils';
import { setAccessToken } from '@/lib/auth/session';
import { setPendingLogin } from '@/lib/auth/pre-auth';
import { ROUTES } from '@/lib/constants/routes';

/**
 * Email-verification mutation (MYN-46 A2). On success the backend returns a
 * session (email verify doubles as first 2FA); we persist the token, seed the
 * auth cache, and route to the verified-success screen.
 */
export function useVerifyEmail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyEmail,
    onSuccess: (outcome, { email }) => {
      // Registration's sign-in was held, so setup is next with no second
      // sign-in (FR-217), exactly as after a login answering setupRequired.
      // The success screen says so first, then leads to setup.
      if ('requires2fa' in outcome && outcome.setupRequired) {
        setPendingLogin({ challenge: outcome, email });
        navigate(ROUTES.AUTH.EMAIL_VERIFIED, { state: { setupRequired: true } });
        return;
      }
      // With 2FA on and the hold gone (or any challenge that is not setup),
      // there is no session yet: the success page sends the user to sign in,
      // which leads to setup (MYN-241).
      if (isSignInRequired(outcome) || 'requires2fa' in outcome) {
        navigate(ROUTES.AUTH.EMAIL_VERIFIED, { state: { signInRequired: true } });
        return;
      }
      setAccessToken(outcome.accessToken);
      queryClient.setQueryData(['auth', 'me'], outcome.user);
      navigate(ROUTES.AUTH.EMAIL_VERIFIED);
    },
  });
}
